import { useCallback, useState, useEffect, useMemo } from "react"
import { notify } from "shared/ui/Notifications/notify"
import { createSlideId, createElementId } from "utils/idGenerators"
import {
  DashboardConfig,
  DashboardConfigChange,
  Slide,
  Template,
} from "types/ReportingDashboard/reporting.types"
import { SlideElement } from "types/ReportingDashboard/elements.types"

/**
 * Parameters for the useSlideManagement hook
 */
interface SlideManagementParams {
  /** Current dashboard configuration */
  dashboardConfig: DashboardConfig
  /** Callback to update dashboard configuration */
  onDashboardConfigChange: (change: DashboardConfigChange) => void
  /** ID of the currently active slide */
  activeSlideId: string | null
  /** Function to update active slide ID */
  setActiveSlideId: (slideId: string | null) => void
  /** ID of the currently active template */
  activeTemplate: string | null
  /** Whether a template is currently being edited */
  isEditingTemplate: boolean
  /** ID of the template being edited */
  editingTemplate: string | null
  /** Original state of template before editing */
  originalTemplateState: Template | null
}

/**
 * Return type for the useSlideManagement hook
 */
interface SlideManagementReturn {
  /** Array of current slides */
  currentSlides: Slide[]
  /** Object containing current templates */
  currentTemplates: Record<string, Template>
  /** Currently active slide object */
  activeSlide: Slide | undefined
  /** Whether slides are currently switching (for loading states) */
  isSwitchingSlides: boolean
  /** Function to safely switch to a different slide */
  safeSetActiveSlide: (slide: Slide) => void
  /** Function to add a new slide */
  handleSlideAdd: () => void
  /** Function to delete a slide by ID */
  handleSlideDelete: (slideId: string) => void
  /** Function to duplicate an existing slide */
  handleSlideDuplicate: (slide: Slide) => void
}

/**
 * Custom hook for managing slide operations including creation, deletion, duplication, and navigation.
 *
 * Handles safe slide switching with template editing state management, prevents data loss during
 * template editing, manages slide transitions with loading states, and maintains consistency
 * between slides and templates.
 *
 * @function useSlideManagement
 * @param params - Configuration object for slide management
 * @returns Object containing slide state and management functions
 */
export const useSlideManagement = ({
  dashboardConfig,
  onDashboardConfigChange,
  activeSlideId,
  setActiveSlideId,
  activeTemplate,
  isEditingTemplate,
  editingTemplate,
  originalTemplateState,
}: SlideManagementParams): SlideManagementReturn => {
  /** Slide switching state to show loading indicators */
  const [isSwitchingSlides, setIsSwitchingSlides] = useState<boolean>(false)

  /** Memoized current slides array */
  const currentSlides = useMemo(
    (): Slide[] => dashboardConfig.slides || [],
    [dashboardConfig.slides]
  )

  /** Memoized current templates object */
  const currentTemplates = useMemo(
    (): Record<string, Template> => dashboardConfig.templates || {},
    [dashboardConfig.templates]
  )

  /** Memoized active slide object */
  const activeSlide = useMemo(
    (): Slide | undefined =>
      currentSlides.find((slide: Slide) => slide.id === activeSlideId) ||
      currentSlides[0],
    [currentSlides, activeSlideId]
  )

  /**
   * Safely switches to a different slide while preserving template editing state.
   * Handles template state restoration if switching away from an edited template.
   *
   * @function safeSetActiveSlide
   * @param slide - The slide to switch to
   */
  const safeSetActiveSlide = useCallback(
    (slide: Slide): void => {
      if (!slide || !slide.id || slide.id === activeSlideId) return

      setIsSwitchingSlides(true)
      const wasEditingTemplate: boolean = isEditingTemplate
      const templateToRestoreId: string | null = editingTemplate
      const stateToRestore: Template | null = originalTemplateState

      setActiveSlideId(slide.id)

      // Restore template state if switching away from template editing
      if (wasEditingTemplate && templateToRestoreId && stateToRestore) {
        const newTemplates: Record<string, Template> = {
          ...currentTemplates,
          [templateToRestoreId]: stateToRestore,
        }
        if (currentTemplates[templateToRestoreId]) {
          onDashboardConfigChange({
            slides: currentSlides,
            templates: newTemplates,
          })
        }
      }
    },
    [
      activeSlideId,
      isEditingTemplate,
      editingTemplate,
      originalTemplateState,
      currentTemplates,
      currentSlides,
      onDashboardConfigChange,
      setActiveSlideId,
    ]
  )

  /**
   * Auto-reset switching state after slide change
   */
  useEffect(() => {
    if (isSwitchingSlides) {
      const timer: NodeJS.Timeout = setTimeout(
        () => setIsSwitchingSlides(false),
        100
      )
      return () => clearTimeout(timer)
    }
  }, [activeSlideId, isSwitchingSlides])

  /**
   * Adds a new slide with the currently active template.
   * Creates a unique ID and applies the current template if available.
   *
   * @function handleSlideAdd
   */
  const handleSlideAdd = useCallback((): void => {
    const newSlideId: string = createSlideId()
    const newSlide: Slide = {
      id: newSlideId,
      templateId: activeTemplate,
      slideElements: [],
      slideTableConfigs: {},
    }
    const newSlidesArray: Slide[] = [...currentSlides, newSlide]
    onDashboardConfigChange({
      slides: newSlidesArray,
      templates: currentTemplates,
    })
    safeSetActiveSlide(newSlide)
  }, [
    activeTemplate,
    safeSetActiveSlide,
    currentSlides,
    currentTemplates,
    onDashboardConfigChange,
  ])

  /**
   * Deletes a slide by ID with safety checks.
   * Prevents deletion of the last slide and handles active slide updates.
   *
   * @function handleSlideDelete
   * @param slideId - ID of the slide to delete
   */
  const handleSlideDelete = useCallback(
    (slideId: string): void => {
      if (currentSlides.length === 1) {
        notify.toast.info("Cannot delete the last slide.")
        return
      }

      const slideIndex: number = currentSlides.findIndex(
        (s: Slide) => s.id === slideId
      )
      if (slideIndex === -1) return

      const newSlidesArray: Slide[] = currentSlides.filter(
        (s: Slide) => s.id !== slideId
      )
      onDashboardConfigChange({
        slides: newSlidesArray,
        templates: currentTemplates,
      })

      // Update active slide if the deleted slide was active
      if (activeSlideId === slideId) {
        const newActiveIndex: number = Math.min(
          slideIndex,
          newSlidesArray.length - 1
        )
        if (newSlidesArray.length > 0) {
          safeSetActiveSlide(newSlidesArray[newActiveIndex])
        } else {
          setActiveSlideId(null)
        }
      }
    },
    [
      currentSlides,
      currentTemplates,
      activeSlideId,
      safeSetActiveSlide,
      onDashboardConfigChange,
      setActiveSlideId,
    ]
  )

  /**
   * Duplicates an existing slide with new IDs for all elements.
   * Deep clones slide elements and configs, assigns new IDs to prevent conflicts.
   *
   * @function handleSlideDuplicate
   * @param slide - The slide to duplicate
   */
  const handleSlideDuplicate = useCallback(
    (slide: Slide): void => {
      const newSlideId: string = createSlideId()
      const clonedElements = JSON.parse(
        JSON.stringify(slide.slideElements || [])
      )
      const clonedConfigs = JSON.parse(
        JSON.stringify(slide.slideTableConfigs || {})
      )

      // Generate new IDs for all elements to prevent conflicts
      const newSlideElements = clonedElements.map((element: SlideElement) => ({
        ...element,
        id: createElementId(element.type),
      }))

      const newSlide: Slide = {
        ...slide,
        id: newSlideId,
        slideElements: newSlideElements,
        slideTableConfigs: clonedConfigs,
      }

      const newSlidesArray: Slide[] = [...currentSlides, newSlide]
      onDashboardConfigChange({
        slides: newSlidesArray,
        templates: currentTemplates,
      })
      safeSetActiveSlide(newSlide)
    },
    [
      safeSetActiveSlide,
      currentSlides,
      currentTemplates,
      onDashboardConfigChange,
    ]
  )

  return {
    currentSlides,
    currentTemplates,
    activeSlide,
    isSwitchingSlides,
    safeSetActiveSlide,
    handleSlideAdd,
    handleSlideDelete,
    handleSlideDuplicate,
  }
}
